import type { AxiosInstance } from 'axios';
import {
  CyclingEvent, CreateEventRequest, UpdateEventRequest, DistanceRequest, EventCategory, EventLink, EventStatus,
  EventStatusFilter, EventType, NotifyPreview, PageResponse, PromotionTier, RegistrationMode, RegistrationState,
  RegistrationStartEntry, RegistrationStartOverview, SetEventPromotionRequest, NewsPost, NewsStatus,
  CreateNewsPostRequest, UpdateNewsPostRequest, NewsCategory, NewsLinkedEvent, ProRace, CreateProRaceRequest,
  ProRaceEnrichmentProposal, ProRaceStage, ProRaceStreamingProvider, UpsertStreamingProviderRequest, Organizer,
} from '../types';
import { ownDistanceEventType, NEWS_CATEGORY_LABELS } from '../brand';

/**
 * Abrufe der Verwaltung, Bereich Inhalte: Events, Anmeldestarts, Rennkalender, News.
 * Einmal für Web und App; die App ruft sie über `create…Service(api)` mit ihrer eigenen Axios-Instanz.
 * Typen, die es in `types.ts` noch nicht gibt, stehen hier im Bereich.
 *
 * Neben den Abrufen steht hier, was das Web bisher in Seiten und `utils/` rechnet und die App
 * genauso braucht: Formularstand ↔ Request eines Events (`eventFormAus`, `eventRequestAus`),
 * die Einordnung der Anmeldestarts und der Zeitzustand eines Profi-Rennens. Ohne DOM-Bezug,
 * damit es nicht ein zweites Mal entsteht.
 */

// ── Events: Liste ──────────────────────────────────────────────────────────

/** Filter der Event-Liste der Verwaltung — dieselben Parameter wie `/events/manage` im Web. */
export interface EventListenFilter {
  suche?: string;
  kategorie?: EventCategory | '';
  land?: string;
  bundesland?: string;
  /** Leer = nur online (Standard). */
  status?: EventStatusFilter | '';
  vergangene?: boolean;
  ohneVeranstalter?: boolean;
  ohneKoordinate?: boolean;
  /** `'__NONE__'` = ohne Eventart. */
  eventart?: string;
  organizerId?: number | null;
  sortierung?: 'standard' | 'datum-auf' | 'datum-ab' | 'score-auf' | 'score-ab';
}

/** Wie viele Filter außer der Suche greifen — die Zahl am Filter-Knopf. */
export function eventFilterAnzahl(f: EventListenFilter): number {
  return [f.kategorie, f.land, f.bundesland, f.status, f.vergangene, f.ohneVeranstalter, f.ohneKoordinate,
    f.eventart, f.organizerId != null ? 'x' : ''].filter(Boolean).length;
}

// ── Events: Formular ───────────────────────────────────────────────────────

/** Eine Preisstufe als Eingabe: Bezeichnung und letzter Tag. */
export interface PreisstufeEingabe { label: string; validUntil: string }

/**
 * Der Formularstand eines Events, wie ihn die App bearbeitet. Dieselben Felder wie
 * `EventFormValues` im Web. Hochgeladene Dateien (Bild, Flyer, GPX) gehören nicht dazu: sie
 * hängen am gespeicherten Event und haben eigene Endpunkte; der Fokuspunkt dagegen schon.
 */
export interface EventFormWerte {
  title: string;
  description: string;
  locationName: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  bundesland: string;
  eventDate: string;
  endDate: string;
  startTime: string;
  startTimeEnd: string;
  participants: string;
  organizer: string;
  organizerId: number | null;
  /** Nur zur Anzeige: Name des verknüpften Veranstalters. */
  organizerName: string;
  category: EventCategory | '';
  secondaryCategories: EventCategory[];
  eventType: EventType | '';
  organizerUrl: string;
  flyerUrl: string;
  imageUrl: string;
  imageCredit: string;
  imageAiGenerated: boolean;
  imageFocusX: number;
  imageFocusY: number;
  fullyBooked: boolean;
  cancelled: boolean;
  tentative: boolean;
  permanent: boolean;
  seasonFrom: number | null;
  seasonUntil: number | null;
  areaName: string;
  registrationStart: string;
  registrationMode: RegistrationMode;
  registrationState: RegistrationState;
  active: boolean;
  distances: DistanceRequest[];
  komootCollectionUrl: string;
  links: EventLink[];
  priceTiers: PreisstufeEingabe[];
}

export const leereStrecke = (): DistanceRequest => ({ distanceKm: 0, elevationMeters: 0, routeUrl: '', label: '' });

export const leeresEventForm = (): EventFormWerte => ({
  title: '', description: '', locationName: '', street: '', houseNumber: '', postalCode: '', city: '',
  bundesland: '', eventDate: '', endDate: '', startTime: '', startTimeEnd: '', participants: '',
  organizer: '', organizerId: null, organizerName: '', category: '', secondaryCategories: [], eventType: '',
  organizerUrl: '', flyerUrl: '', imageUrl: '', imageCredit: '', imageAiGenerated: false, imageFocusX: 50,
  imageFocusY: 50, fullyBooked: false, cancelled: false, tentative: false, permanent: false, seasonFrom: null,
  seasonUntil: null, areaName: '', registrationStart: '', registrationMode: 'UNKNOWN', registrationState: 'UNKNOWN',
  active: true, distances: [leereStrecke()], komootCollectionUrl: '', links: [], priceTiers: [],
});

/**
 * Füllt das Formular aus einem Event — dieselbe Abbildung wie `buildFormFromEvent` im Web.
 * Bild und Flyer über die **externen** URLs: die aufgelösten zeigen bei einem Upload auf unsere
 * eigene Route und wanderten sonst beim Speichern als „externe URL“ in die Datenbank.
 */
export function eventFormAus(event: CyclingEvent, organizerName?: string): EventFormWerte {
  return {
    title: event.title,
    description: event.description || '',
    locationName: event.locationName || '',
    street: event.street || '',
    houseNumber: event.houseNumber || '',
    postalCode: event.postalCode || '',
    city: event.city || '',
    bundesland: event.bundesland || '',
    eventDate: event.permanent ? '' : event.eventDate,
    endDate: event.permanent ? '' : event.endDate || '',
    startTime: event.startTime || '',
    startTimeEnd: event.startTimeEnd || '',
    participants: event.participants ? String(event.participants) : '',
    organizer: event.organizer || '',
    organizerId: event.organizerId ?? null,
    organizerName: organizerName ?? (event.organizerId ? event.organizer || '' : ''),
    category: event.category,
    secondaryCategories: event.secondaryCategories ?? [],
    eventType: event.eventType || '',
    organizerUrl: event.organizerUrl || '',
    flyerUrl: event.flyerUrlExternal || '',
    imageUrl: event.imageUrlExternal || '',
    imageCredit: event.imageCredit || '',
    imageAiGenerated: !!event.imageAiGenerated,
    imageFocusX: event.imageFocusX ?? 50,
    imageFocusY: event.imageFocusY ?? 50,
    fullyBooked: event.fullyBooked,
    cancelled: event.cancelled,
    tentative: event.tentative,
    permanent: !!event.permanent,
    seasonFrom: event.seasonFrom ?? null,
    seasonUntil: event.seasonUntil ?? null,
    areaName: event.areaName ?? '',
    registrationStart: event.registrationStart || '',
    registrationMode: event.registrationMode ?? 'UNKNOWN',
    registrationState: event.registrationState ?? 'UNKNOWN',
    active: event.active,
    distances: event.distances.length > 0
      ? event.distances.map(d => ({
        id: d.id, distanceKm: d.distanceKm, elevationMeters: d.elevationMeters, routeUrl: d.routeUrl || '',
        label: d.label || '', hasGpx: d.hasGpx, durationMinutes: d.durationMinutes,
        eventType: d.eventType ?? null, startTime: d.startTime ?? null,
      }))
      : [leereStrecke()],
    komootCollectionUrl: event.komootCollectionUrl || '',
    links: event.links ?? [],
    priceTiers: (event.priceTiers ?? []).map(t => ({ label: t.label ?? '', validUntil: t.validUntil })),
  };
}

/**
 * Kopie eines Events als neues: ohne Quell-Ids und GPX (die hängen am Original), ohne
 * hochgeladenes Bild, und ohne SOURCE/BREITENSPORT-Links — sonst hinge die Kopie am nächtlichen
 * Breitensport-Abgleich des Originals. Wie „Kopieren“ im Web.
 */
export function eventFormKopie(event: CyclingEvent): EventFormWerte {
  const basis = eventFormAus(event);
  return {
    ...basis,
    distances: basis.distances.map(d => ({
      distanceKm: d.distanceKm, elevationMeters: d.elevationMeters, routeUrl: d.routeUrl || '', label: d.label || '',
      durationMinutes: d.durationMinutes, eventType: d.eventType ?? null, startTime: d.startTime ?? null,
    })),
    links: (event.links ?? [])
      .filter(l => l.type !== 'SOURCE' && l.type !== 'BREITENSPORT')
      .map(l => ({ url: l.url, type: l.type, visibility: l.visibility, label: l.label })),
  };
}

/** Leere Link-Zeilen fallen weg, der Rest wird getrimmt — das, was ans Backend geht. */
export function bereinigteLinks(links: EventLink[]): EventLink[] {
  return links
    .map(l => ({ ...l, url: l.url.trim(), label: l.label && l.label.trim() ? l.label.trim() : undefined }))
    .filter(l => l.url.length > 0);
}

/**
 * Wird das Speichern ein Vorrücken ins nächste Jahr? Alter Termin zurück, neuer dahinter — dieselbe
 * Regel wie `EventEditionService.isNextEdition` und das Web-Formular. Ein permanentes Event hat
 * keine Auflagen, auch nicht beim Wechsel zu einem Termin (der „alte Termin“ wäre 1970).
 */
export function wirdNeueAuflage(vorher: { eventDate: string; permanent: boolean }, jetzt: { eventDate: string; permanent: boolean }, heute: string): boolean {
  return !jetzt.permanent && !vorher.permanent && !!vorher.eventDate && !!jetzt.eventDate
    && vorher.eventDate < heute && jetzt.eventDate > vorher.eventDate;
}

/**
 * Formular → Request, wie `EventForm.handleSave` im Web. Leerstrings statt `undefined` bei
 * Bild, Anmeldestart: dort heißt `null` im Backend „nicht mitgeschickt“, und das Leeren eines
 * Feldes muss löschen. Links und Preisstufen gehen immer als ganze Liste mit (das Formular zeigt
 * alle, eine leere Liste heißt wirklich „gelöscht“).
 */
export function eventRequestAus(form: EventFormWerte, opts: { newEdition?: boolean } = {}): UpdateEventRequest {
  const strecken = form.distances.filter(d => d.distanceKm > 0 || (d.durationMinutes ?? 0) > 0);
  return {
    title: form.title.trim(),
    description: form.description || undefined,
    locationName: form.locationName || undefined,
    street: form.street || undefined,
    houseNumber: form.houseNumber || undefined,
    postalCode: form.postalCode,
    city: form.city,
    bundesland: form.bundesland,
    eventDate: form.eventDate,
    endDate: form.endDate || undefined,
    startTime: form.startTime || undefined,
    startTimeEnd: form.startTimeEnd || undefined,
    participants: form.participants ? parseInt(form.participants, 10) || 0 : 0,
    organizer: form.organizerId ? undefined : (form.organizer || undefined),
    organizerId: form.organizerId ?? undefined,
    category: form.category as EventCategory,
    secondaryCategories: form.secondaryCategories,
    eventType: form.eventType ? form.eventType as EventType : undefined,
    organizerUrl: form.organizerUrl || undefined,
    flyerUrl: form.flyerUrl || undefined,
    imageUrl: form.imageUrl,
    imageCredit: form.imageCredit,
    imageAiGenerated: form.imageAiGenerated,
    imageFocusX: form.imageFocusX,
    imageFocusY: form.imageFocusY,
    fullyBooked: form.fullyBooked,
    cancelled: form.cancelled,
    tentative: form.permanent ? false : form.tentative,
    permanent: form.permanent,
    seasonFrom: form.permanent ? form.seasonFrom : null,
    seasonUntil: form.permanent ? form.seasonUntil : null,
    areaName: form.permanent ? (form.areaName.trim() || null) : null,
    registrationStart: form.registrationStart,
    registrationMode: form.registrationMode,
    registrationState: form.registrationState,
    distances: strecken.map(d => ({
      ...d,
      routeUrl: d.routeUrl || undefined,
      label: d.label || undefined,
      durationMinutes: d.durationMinutes && d.durationMinutes > 0 ? d.durationMinutes : undefined,
      startTime: d.startTime || null,
      eventType: ownDistanceEventType(form.eventType, d.eventType) as EventType | null,
    })),
    komootCollectionUrl: form.komootCollectionUrl || undefined,
    links: bereinigteLinks(form.links),
    priceTiers: form.priceTiers.filter(r => r.validUntil).map(r => ({ label: r.label.trim() || null, validUntil: r.validUntil })),
    active: form.active,
    newEdition: opts.newEdition,
  };
}

/** Was vor dem Speichern fehlt — `null`, wenn alles da ist. Dieselben Pflichtfelder wie das Backend. */
export function eventFormFehlt(form: EventFormWerte): string | null {
  const fehlt: string[] = [];
  if (!form.title.trim()) fehlt.push('Titel');
  if (!form.category) fehlt.push('Kategorie');
  if (!form.permanent && !form.eventDate) fehlt.push('Datum');
  if (!form.bundesland) fehlt.push('(Bundes)Land');
  if (!form.permanent && !form.postalCode.trim()) fehlt.push('PLZ');
  if (!form.permanent && !form.city.trim()) fehlt.push('Ort');
  if (!fehlt.length) {
    if (form.endDate && form.eventDate && form.endDate < form.eventDate) return 'Das Enddatum liegt vor dem Startdatum.';
    return null;
  }
  return `Es fehlt noch: ${fehlt.join(', ')}.`;
}

/** „läuft gerade online“ etc. — die drei Stände, wie sie in der Verwaltung heißen. */
export const EVENT_STATUS_NAMEN: Record<EventStatus, string> = {
  [EventStatus.PUBLISHED]: 'Online',
  [EventStatus.DRAFT]: 'Offline, Entwurf',
  [EventStatus.ARCHIVED]: 'Offline, archiviert',
};

// ── Anmeldestarts ──────────────────────────────────────────────────────────

export type AnmeldeAbschnitt = 'OPEN' | 'SOON' | 'LATER';
export const ANMELDE_BALD_TAGE = 30;

export const ANMELDE_ABSCHNITTE: { key: AnmeldeAbschnitt; titel: string; hinweis: string }[] = [
  { key: 'OPEN', titel: 'Anmeldung läuft bereits', hinweis: 'Termin erreicht oder als „läuft bereits“ gepflegt.' },
  { key: 'SOON', titel: `In den nächsten ${ANMELDE_BALD_TAGE} Tagen`, hinweis: 'Hier lohnt sich der Blick auf Ankündigung und Kanäle.' },
  { key: 'LATER', titel: 'Später', hinweis: 'Vorgemerkt, noch nichts zu tun.' },
];

/**
 * Einordnung einer Zeile der Anmeldestart-Übersicht — dieselbe Rechnung wie `bucketOfEntry` im
 * Web: gerechnet auf den Moment, ab dem die Anmeldung offen ist („Dezember 2026“ ab dem 1.12.
 * um 0 Uhr, „1.12., 9:00“ ab 9 Uhr).
 */
export function anmeldeAbschnitt(
  e: { registrationState: RegistrationState; registrationStart?: string | null; registrationStartDate?: string | null },
  jetzt: Date,
): AnmeldeAbschnitt {
  if (e.registrationState === 'OPEN') return 'OPEN';
  const wert = e.registrationStart || e.registrationStartDate;
  if (!wert) return 'OPEN';
  const mitZeit = wert.indexOf('T') >= 0;
  // „2026“ bzw. „2026-12“ auf einen vollen Tag ergänzen, sonst ist das Datum ungültig.
  const tag = mitZeit ? wert : (wert.length === 4 ? `${wert}-01-01` : wert.length === 7 ? `${wert}-01` : wert);
  const ab = new Date(mitZeit ? `${tag}:00` : `${tag}T00:00:00`);
  if (ab.getTime() <= jetzt.getTime()) return 'OPEN';
  const tage = Math.ceil((ab.getTime() - jetzt.getTime()) / 86400000);
  return tage <= ANMELDE_BALD_TAGE ? 'SOON' : 'LATER';
}

/** „in 12 Tagen“ / „heute“ / „seit 3 Tagen“ — nur bei taggenauer Angabe, sonst wäre es erfunden. */
export function anmeldeAbstand(e: RegistrationStartEntry, heute: Date): string | null {
  if (!e.registrationStart || !e.registrationStartDate || e.registrationStart.length < 10) return null;
  const tage = Math.round((new Date(`${e.registrationStartDate}T00:00:00`).getTime() - heute.getTime()) / 86400000);
  if (tage === 0) return 'heute';
  if (tage === 1) return 'morgen';
  if (tage > 1) return `in ${tage} Tagen`;
  if (tage === -1) return 'seit gestern';
  return `seit ${Math.abs(tage)} Tagen`;
}

export const ANMELDE_ART_KURZ: Record<RegistrationMode, string> = {
  UNKNOWN: 'Anmeldeart unbekannt',
  ONLINE: 'Online',
  ONSITE_ONLY: 'Nur vor Ort',
};

// ── Profi-Rennkalender ─────────────────────────────────────────────────────

export type RennZustand = 'RUNNING' | 'UPCOMING' | 'PAST';
export type RennFilter = 'ALL' | RennZustand | 'DRAFT';

export const RENN_ZUSTAND_NAMEN: Record<RennZustand, string> = {
  RUNNING: 'Läuft gerade',
  UPCOMING: 'Kommend',
  PAST: 'Abgeschlossen',
};

export const RENN_TYPEN: { key: string; label: string }[] = [
  { key: 'ETAPPENRENNEN', label: 'Etappenrennen' },
  { key: 'EINTAGESRENNEN', label: 'Eintagesrennen' },
];

/** Zeitzustand eines Rennens; Kalendertage als lokale Mitternacht, das Ende gilt bis 23:59. */
export function rennZustand(start: string, ende: string, jetzt: Date = new Date()): RennZustand {
  if (new Date(`${ende}T23:59:59`) < jetzt) return 'PAST';
  return new Date(`${start}T00:00:00`) <= jetzt ? 'RUNNING' : 'UPCOMING';
}

/** „Startet heute“, „In 12 Tagen“, „Läuft noch 3 Tage“ — null für ein abgeschlossenes Rennen. */
export function rennAbstand(start: string, ende: string, jetzt: Date = new Date()): string | null {
  const heute = new Date(jetzt.getTime());
  heute.setHours(0, 0, 0, 0);
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${ende}T00:00:00`);
  if (e < heute) return null;
  if (s.getTime() === heute.getTime()) return 'Startet heute';
  if (s <= heute) {
    const rest = Math.ceil((e.getTime() - heute.getTime()) / 86400000);
    return rest === 0 ? 'Heute letzter Tag' : `Läuft noch ${rest} Tag${rest === 1 ? '' : 'e'}`;
  }
  const tage = Math.ceil((s.getTime() - heute.getTime()) / 86400000);
  return tage === 1 ? 'Morgen' : `In ${tage} Tagen`;
}

/** Filtert und gruppiert die Rennliste nach Zustand (laufend → kommend → abgeschlossen). */
export function rennenGruppiert(rennen: ProRace[], suche: string, filter: RennFilter, jetzt: Date = new Date()): { zustand: RennZustand; rennen: ProRace[] }[] {
  const term = suche.trim().toLowerCase();
  const sichtbar = rennen.filter(r => {
    if (term && ![r.name, r.category, r.country, r.winner].some(v => (v ?? '').toLowerCase().indexOf(term) >= 0)) return false;
    if (filter === 'ALL') return true;
    if (filter === 'DRAFT') return r.status !== 'PUBLISHED';
    return rennZustand(r.startDate, r.endDate, jetzt) === filter;
  });
  const reihe: RennZustand[] = ['RUNNING', 'UPCOMING', 'PAST'];
  return reihe.map(z => {
    const liste = sichtbar.filter(r => rennZustand(r.startDate, r.endDate, jetzt) === z);
    // Kommende: das nächste zuerst. Abgeschlossene: das zuletzt gefahrene zuerst, dort liegt die Nacharbeit.
    liste.sort((a, b) => z === 'PAST' ? b.startDate.localeCompare(a.startDate) : a.startDate.localeCompare(b.startDate));
    return { zustand: z, rennen: liste };
  }).filter(g => g.rennen.length > 0);
}

export function rennenZaehlen(rennen: ProRace[], jetzt: Date = new Date()): Record<RennFilter, number> {
  const c: Record<RennFilter, number> = { ALL: rennen.length, RUNNING: 0, UPCOMING: 0, PAST: 0, DRAFT: 0 };
  rennen.forEach(r => {
    c[rennZustand(r.startDate, r.endDate, jetzt)] += 1;
    if (r.status !== 'PUBLISHED') c.DRAFT += 1;
  });
  return c;
}

/** Slug-Vorschlag aus dem Namen; das Jahr hängt an, weil jede Saison eine eigene Seite bekommt. */
export function rennSlug(name: string, jahr: string): string {
  const basis = name.toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return basis ? `${basis}-${jahr}` : '';
}

export const leeresRennen = (): CreateProRaceRequest => ({
  name: '', slug: '', raceType: 'ETAPPENRENNEN', category: 'UCI WorldTour', startDate: '', endDate: '',
  broadcastInfo: '', description: '', websiteUrl: '', wikipediaUrl: '', winner: '', status: 'PUBLISHED',
});

export function rennenFormAus(r: ProRace): CreateProRaceRequest {
  return {
    name: r.name, slug: r.slug, raceType: r.raceType, category: r.category ?? '', startDate: r.startDate,
    endDate: r.endDate, broadcastInfo: r.broadcastInfo ?? '', description: r.description ?? '',
    websiteUrl: r.websiteUrl ?? '', wikipediaUrl: r.wikipediaUrl ?? '', winner: r.winner ?? '', status: r.status,
    // Diese drei pflegt der Agent; sie laufen unverändert mit, damit Speichern sie nicht leert.
    country: r.country, totalDistanceKm: r.totalDistanceKm, imageUrl: r.imageUrl,
  };
}

// ── Rennkalender: Etappen ──────────────────────────────────────────────────

/** Etappentypen wie `StagesPanel` im Web; der Schlüssel steht so in der Datenbank. */
export const ETAPPEN_TYPEN: { key: string; label: string }[] = [
  { key: 'FLACH', label: 'Flach' },
  { key: 'HÜGELIG', label: 'Hügelig' },
  { key: 'BERG', label: 'Bergetappe' },
  { key: 'ZEITFAHREN', label: 'Zeitfahren' },
  { key: 'PROLOG', label: 'Prolog' },
];

/** Formularstand einer Etappe; Zahlen als Text, leer heißt „ohne Angabe“. */
export interface EtappeFormWerte {
  stageNumber: string;
  stageDate: string;
  startCity: string;
  endCity: string;
  distanceKm: string;
  elevationMeters: string;
  stageType: string;
  winner: string;
  startTime: string;
  estimatedArrivalTime: string;
  description: string;
  status: string;
}

/** Was `PUT /pro-races/{id}/stages/{nummer}` erwartet (UpsertStageRequest). */
export interface EtappeRequest {
  stageNumber: number;
  stageDate?: string;
  startCity?: string;
  endCity?: string;
  distanceKm?: number;
  elevationMeters?: number;
  stageType?: string;
  winner?: string;
  startTime?: string;
  estimatedArrivalTime?: string;
  description?: string;
  status: string;
}

/** Neue Etappe: die nächste freie Nummer, sonst leer (0 = Prolog). */
export function leereEtappe(vorhanden: ProRaceStage[]): EtappeFormWerte {
  const naechste = vorhanden.length ? Math.max(...vorhanden.map(s => s.stageNumber)) + 1 : 1;
  return {
    stageNumber: String(naechste), stageDate: '', startCity: '', endCity: '', distanceKm: '', elevationMeters: '',
    stageType: '', winner: '', startTime: '', estimatedArrivalTime: '', description: '', status: 'PUBLISHED',
  };
}

export function etappeFormAus(s: ProRaceStage): EtappeFormWerte {
  return {
    stageNumber: String(s.stageNumber), stageDate: s.stageDate ?? '', startCity: s.startCity ?? '', endCity: s.endCity ?? '',
    distanceKm: s.distanceKm != null ? String(s.distanceKm) : '', elevationMeters: s.elevationMeters != null ? String(s.elevationMeters) : '',
    stageType: s.stageType ?? '', winner: s.winner ?? '', startTime: s.startTime ?? '',
    estimatedArrivalTime: s.estimatedArrivalTime ?? '', description: s.description ?? '', status: s.status ?? 'PUBLISHED',
  };
}

/** Formular → Request. `null`, wenn die Nummer fehlt (sie ist der Schlüssel der Etappe). */
export function etappeRequestAus(f: EtappeFormWerte): EtappeRequest | null {
  const nummer = parseInt(f.stageNumber, 10);
  if (isNaN(nummer) || nummer < 0) return null;
  const text = (v: string) => (v.trim() ? v.trim() : undefined);
  const zahl = (v: string) => { const n = parseInt(v, 10); return isNaN(n) ? undefined : n; };
  return {
    stageNumber: nummer, stageDate: text(f.stageDate), startCity: text(f.startCity), endCity: text(f.endCity),
    distanceKm: zahl(f.distanceKm), elevationMeters: zahl(f.elevationMeters), stageType: text(f.stageType),
    winner: text(f.winner), startTime: text(f.startTime), estimatedArrivalTime: text(f.estimatedArrivalTime),
    description: text(f.description), status: f.status || 'PUBLISHED',
  };
}

/** „Prolog“ für Nummer 0, sonst „Etappe N“. */
export const etappeName = (nummer: number): string => (nummer === 0 ? 'Prolog' : `Etappe ${nummer}`);

/** Eine Zeile „alt → neu“ eines Agenten-Vorschlags zum Rennkalender. */
export interface RennVorschlagZeile { label: string; neu: string; alt?: string }

export function rennVorschlagZeilen(p: ProRaceEnrichmentProposal, rennen: ProRace | undefined): RennVorschlagZeile[] {
  const etappe = p.proposalType === 'STAGE' && rennen ? (rennen.stages ?? []).filter(s => s.id === p.stageId)[0] : undefined;
  const km = (v?: number) => (v != null ? `${v} km` : undefined);
  const m = (v?: number) => (v != null ? `${v} m` : undefined);
  const zeilen: [string, string | undefined, string | undefined][] = p.proposalType === 'STAGE'
    ? [
      ['Etappe', p.proposedStageNumber != null ? `Etappe ${p.proposedStageNumber}` : undefined, etappe ? `Etappe ${etappe.stageNumber}` : undefined],
      ['Datum', p.proposedStageDate, etappe?.stageDate],
      ['Start', p.proposedStartCity, etappe?.startCity],
      ['Ziel', p.proposedEndCity, etappe?.endCity],
      ['Distanz', km(p.proposedDistanceKm), km(etappe?.distanceKm)],
      ['Höhenmeter', m(p.proposedElevationMeters), m(etappe?.elevationMeters)],
      ['Typ', p.proposedStageType, etappe?.stageType],
    ]
    : [
      ['Name', p.proposedName, rennen?.name],
      ['Beschreibung', p.proposedDescription, rennen?.description],
      ['Übertragung', p.proposedBroadcastInfo, rennen?.broadcastInfo],
      ['Website', p.proposedWebsiteUrl, rennen?.websiteUrl],
      ['Wikipedia', p.proposedWikipediaUrl, rennen?.wikipediaUrl],
      ['Sieger', p.proposedWinner, rennen?.winner],
      ['Kategorie', p.proposedCategory, rennen?.category],
    ];
  return zeilen
    .filter(z => z[1] != null && z[1] !== '')
    .map(([label, neu, alt]) => ({ label, neu: neu as string, alt: alt && alt !== neu ? alt : undefined }));
}

// ── News ───────────────────────────────────────────────────────────────────

export type NewsFilter = 'ALL' | NewsStatus;

export const NEWS_STATUS_NAMEN: Record<NewsStatus, string> = {
  [NewsStatus.DRAFT]: 'Entwurf',
  [NewsStatus.PUBLISHED]: 'Veröffentlicht',
  [NewsStatus.ARCHIVED]: 'Archiviert',
};

/** Statuswechsel je Stand, wie in der Web-Verwaltung. */
export const NEWS_STATUS_WECHSEL: Record<NewsStatus, { nach: NewsStatus; label: string }[]> = {
  [NewsStatus.DRAFT]: [{ nach: NewsStatus.PUBLISHED, label: 'Veröffentlichen' }],
  [NewsStatus.PUBLISHED]: [
    { nach: NewsStatus.DRAFT, label: 'Auf Entwurf zurücksetzen' },
    { nach: NewsStatus.ARCHIVED, label: 'Archivieren' },
  ],
  [NewsStatus.ARCHIVED]: [{ nach: NewsStatus.PUBLISHED, label: 'Veröffentlichen' }],
};

/** Hochgeladenes Cover (unsere Route) im Gegensatz zu einer externen Bild-URL. */
export const hatHochgeladenesCover = (post: { coverImageUrl?: string | null } | null): boolean =>
  !!post?.coverImageUrl && post.coverImageUrl.indexOf('/api/v1/news/') >= 0;

/** Gruppiert nach Status, Entwürfe zuerst (dort liegt die Arbeit), sonst neueste zuerst. */
export function newsGruppiert(posts: NewsPost[], suche: string, filter: NewsFilter): { status: NewsStatus; posts: NewsPost[] }[] {
  const term = suche.trim().toLowerCase();
  const datum = (p: NewsPost) => p.publishedAt ?? p.createdAt ?? '';
  const sichtbar = posts.filter(p => {
    if (filter !== 'ALL' && p.status !== filter) return false;
    if (!term) return true;
    const felder = [p.title, p.slug, p.authorName, NEWS_CATEGORY_LABELS[p.category]]
      .concat((p.linkedEvents ?? []).map(e => e.title));
    return felder.some(v => (v ?? '').toLowerCase().indexOf(term) >= 0);
  });
  return [NewsStatus.DRAFT, NewsStatus.PUBLISHED, NewsStatus.ARCHIVED]
    .map(status => ({ status, posts: sichtbar.filter(p => p.status === status).sort((a, b) => datum(b).localeCompare(datum(a))) }))
    .filter(g => g.posts.length > 0);
}

export function newsZaehlen(posts: NewsPost[]): Record<NewsFilter, number> {
  const c = { ALL: posts.length, [NewsStatus.DRAFT]: 0, [NewsStatus.PUBLISHED]: 0, [NewsStatus.ARCHIVED]: 0 } as Record<NewsFilter, number>;
  posts.forEach(p => { c[p.status] += 1; });
  return c;
}

export interface NewsFormWerte {
  title: string;
  slug: string;
  teaser: string;
  content: string;
  coverImageUrl: string;
  photoCredit: string;
  category: NewsCategory;
  authorName: string;
  justified: boolean;
  linkedEvents: NewsLinkedEvent[];
}

export const leeresNewsForm = (): NewsFormWerte => ({
  title: '', slug: '', teaser: '', content: '', coverImageUrl: '', photoCredit: '', category: NewsCategory.SZENE,
  authorName: 'Radsport Events Redaktion', justified: false, linkedEvents: [],
});

export function newsFormAus(p: NewsPost): NewsFormWerte {
  return {
    title: p.title, slug: p.slug, teaser: p.teaser ?? '', content: p.content,
    // Die Route eines hochgeladenen Covers gehört nicht ins URL-Feld, dort stehen nur externe Bilder.
    coverImageUrl: hatHochgeladenesCover(p) ? '' : (p.coverImageUrl ?? ''),
    photoCredit: p.photoCredit ?? '', category: p.category, authorName: p.authorName,
    justified: p.justified ?? false, linkedEvents: p.linkedEvents ?? [],
  };
}

/** Formular → Request. `linkedEventIds` geht immer mit: die Verknüpfung wird nur am Artikel gesetzt. */
export function newsRequestAus(f: NewsFormWerte): UpdateNewsPostRequest {
  return {
    slug: f.slug.trim(),
    title: f.title.trim(),
    teaser: f.teaser.trim() || null,
    content: f.content.trim(),
    coverImageUrl: f.coverImageUrl.trim() || null,
    photoCredit: f.photoCredit.trim() || null,
    category: f.category,
    authorName: f.authorName.trim() || undefined,
    justified: f.justified,
    linkedEventIds: f.linkedEvents.map(e => e.id),
  };
}

export function newsVerknuepfungAus(ev: CyclingEvent): NewsLinkedEvent {
  return { id: ev.id, title: ev.title, eventDate: ev.eventDate, endDate: ev.endDate ?? null, city: ev.city, bundesland: ev.bundesland, category: ev.category, status: ev.status };
}

// ── Abrufe ─────────────────────────────────────────────────────────────────

export function createInhalteService(api: AxiosInstance) {
  return {
    // Events
    events: async (f: EventListenFilter, page: number, size = 20): Promise<PageResponse<CyclingEvent>> => {
      const params: Record<string, unknown> = { page, size };
      if (f.status === 'ALL') params.allStatuses = true;
      else if (f.status) params.status = f.status;
      if (f.suche && f.suche.trim()) params.search = f.suche.trim();
      if (f.kategorie) params.category = f.kategorie;
      if (f.land) params.country = f.land;
      if (f.bundesland) params.bundesland = f.bundesland;
      if (f.vergangene) params.includePast = true;
      if (f.ohneVeranstalter) params.noOrganizer = true;
      if (f.ohneKoordinate) params.noCoordinates = true;
      if (f.eventart === '__NONE__') params.noEventType = true;
      else if (f.eventart) params.eventType = f.eventart;
      if (f.organizerId != null) params.organizerId = f.organizerId;
      const s = f.sortierung ?? 'standard';
      if (s !== 'standard') {
        params.sortBy = s.indexOf('score') === 0 ? 'score' : 'date';
        params.sortDir = s.slice(-2) === 'ab' ? 'desc' : 'asc';
      }
      return (await api.get('/events', { params })).data;
    },
    event: async (id: number): Promise<CyclingEvent> => (await api.get(`/events/${id}`)).data,
    eventAnlegen: async (data: CreateEventRequest): Promise<CyclingEvent> => (await api.post('/events', data)).data,
    eventSpeichern: async (id: number, data: UpdateEventRequest, benachrichtigen: boolean): Promise<CyclingEvent> =>
      (await api.put(`/events/${id}`, data, { params: { notifyFavorites: benachrichtigen } })).data,
    /** Würde genau dieser Stand eine Mail an die Favoriten auslösen? Schreibt nichts. */
    benachrichtigungVorschau: async (id: number, data: UpdateEventRequest): Promise<NotifyPreview> => {
      const r = (await api.post(`/events/${id}/notify-preview`, data)).data;
      return { count: r?.count ?? 0, alreadyNotifiedToday: r?.alreadyNotifiedToday === true, wouldNotify: r?.wouldNotify === true };
    },
    eventStatus: async (id: number, status: EventStatus): Promise<CyclingEvent> =>
      (await api.patch(`/events/${id}/status`, null, { params: { status } })).data,
    eventLoeschen: async (id: number): Promise<void> => { await api.delete(`/events/${id}`); },
    /** Aufs nächste Jahr fortschreiben; 409, wenn die Auflage schon als eigenes Event existiert. */
    eventFortschreiben: async (id: number): Promise<CyclingEvent> =>
      (await api.post(`/events/${id}/roll-forward`, null, { params: { years: 1 } })).data,
    /** Hochgeladenes Bild entfernen; zurück kommt das Event (mit internen Links, wie nach jedem Admin-Schreibweg). */
    eventBildEntfernen: async (id: number): Promise<CyclingEvent> => (await api.delete(`/events/${id}/image`)).data,
    eventFlyerEntfernen: async (id: number): Promise<CyclingEvent> => (await api.delete(`/events/${id}/flyer`)).data,
    streckeGpxEntfernen: async (eventId: number, streckeId: number): Promise<void> => {
      await api.delete(`/events/${eventId}/distances/${streckeId}/gpx`);
    },
    hervorhebungSetzen: async (id: number, data: SetEventPromotionRequest): Promise<CyclingEvent> =>
      (await api.put(`/events/${id}/promotion`, data)).data,
    hervorhebungBeenden: async (id: number): Promise<CyclingEvent> => (await api.delete(`/events/${id}/promotion`)).data,
    aehnliche: async (title: string, eventDate: string, bundesland: string, excludeId?: number, category?: EventCategory): Promise<CyclingEvent[]> => {
      const params: Record<string, unknown> = { title, eventDate, bundesland };
      if (excludeId != null) params.excludeId = excludeId;
      if (category) params.category = category;
      return (await api.get('/events/find-similar', { params })).data;
    },
    auflagen: async (title: string, eventDate: string, bundesland: string, category?: EventCategory): Promise<CyclingEvent[]> => {
      const params: Record<string, unknown> = { title, eventDate, bundesland };
      if (category) params.category = category;
      return (await api.get('/events/find-editions', { params })).data;
    },
    /** Veranstalter für die Auswahl im Formular (Redaktionsliste, mit Suche). */
    veranstalter: async (suche: string): Promise<Organizer[]> =>
      (await api.get('/organizers/admin', { params: suche.trim() ? { search: suche.trim() } : {} })).data,
    /** Touren einer Komoot-Collection als Strecken-Vorschlag. */
    komootVorschau: async (url: string): Promise<{ name: string; distanceKm: number; elevationMeters: number; komootUrl: string }[]> =>
      (await api.post('/events/komoot-preview', { url })).data,
    /** Event-Suche für Verknüpfungen (News, Rennkalender): nur veröffentlichte. */
    eventsVerknuepfbar: async (suche: string): Promise<CyclingEvent[]> =>
      (await api.get('/events', { params: { search: suche, size: 10, status: 'PUBLISHED' } })).data.content ?? [],

    // Anmeldestarts
    anmeldestarts: async (): Promise<RegistrationStartOverview> => (await api.get('/events/registration-starts')).data,

    // Profi-Rennkalender
    rennen: async (): Promise<ProRace[]> => (await api.get('/pro-races', { params: { all: true } })).data,
    rennenAnlegen: async (data: CreateProRaceRequest): Promise<ProRace> => (await api.post('/pro-races', data)).data,
    rennenSpeichern: async (id: number, data: CreateProRaceRequest): Promise<ProRace> => (await api.put(`/pro-races/${id}`, data)).data,
    rennenLoeschen: async (id: number): Promise<void> => { await api.delete(`/pro-races/${id}`); },
    rennenEventVerknuepfen: async (id: number, eventId: number): Promise<void> => { await api.post(`/pro-races/${id}/link-event`, { eventId }); },
    rennenEventLoesen: async (id: number, eventId: number): Promise<void> => { await api.delete(`/pro-races/${id}/link-event/${eventId}`); },
    /** Etappe anlegen oder ändern; die Nummer ist der Schlüssel (eine vorhandene wird überschrieben). */
    etappeSpeichern: async (rennenId: number, data: EtappeRequest): Promise<ProRaceStage> =>
      (await api.put(`/pro-races/${rennenId}/stages/${data.stageNumber}`, data)).data,
    etappeLoeschen: async (rennenId: number, nummer: number): Promise<void> => { await api.delete(`/pro-races/${rennenId}/stages/${nummer}`); },
    etappeGpxEntfernen: async (rennenId: number, nummer: number): Promise<void> => { await api.delete(`/pro-races/${rennenId}/stages/${nummer}/gpx`); },
    streaming: async (id: number): Promise<ProRaceStreamingProvider[]> => (await api.get(`/pro-races/${id}/streaming-providers`)).data,
    streamingAnlegen: async (id: number, data: UpsertStreamingProviderRequest): Promise<ProRaceStreamingProvider> =>
      (await api.post(`/pro-races/${id}/streaming-providers`, data)).data,
    streamingSpeichern: async (id: number, anbieterId: number, data: UpsertStreamingProviderRequest): Promise<ProRaceStreamingProvider> =>
      (await api.put(`/pro-races/${id}/streaming-providers/${anbieterId}`, data)).data,
    streamingLoeschen: async (id: number, anbieterId: number): Promise<void> => { await api.delete(`/pro-races/${id}/streaming-providers/${anbieterId}`); },
    rennVorschlaege: async (): Promise<ProRaceEnrichmentProposal[]> => (await api.get('/pro-race-enrichments/pending')).data,
    rennVorschlagPruefen: async (id: number, approved: boolean): Promise<void> => { await api.post(`/pro-race-enrichments/${id}/review`, { approved }); },
    rennVorschlaegeAlleAblehnen: async (): Promise<void> => { await api.post('/pro-race-enrichments/reject-all-pending', {}); },

    // News
    /** Alle Artikel der Redaktion (wie das Web: eine Seite mit bis zu 200). */
    news: async (): Promise<NewsPost[]> => (await api.get('/news/admin', { params: { page: 0, size: 200 } })).data.content ?? [],
    newsAnlegen: async (data: CreateNewsPostRequest): Promise<NewsPost> => (await api.post('/news', data)).data,
    newsSpeichern: async (id: number, data: UpdateNewsPostRequest): Promise<NewsPost> => (await api.put(`/news/${id}`, data)).data,
    newsStatus: async (id: number, status: NewsStatus): Promise<NewsPost> => (await api.patch(`/news/${id}/status`, { status })).data,
    newsLoeschen: async (id: number): Promise<void> => { await api.delete(`/news/${id}`); },
    newsCoverEntfernen: async (id: number): Promise<NewsPost> => (await api.delete(`/news/${id}/cover`)).data,
  };
}

export type InhalteService = ReturnType<typeof createInhalteService>;

/** Stufe der Hervorhebung als Request — eine Laufzeit in Tagen oder bis zum Ende des Events. */
export function hervorhebungRequest(tier: PromotionTier, label: string, laufzeit: { tage: number } | 'bisEvent'): SetEventPromotionRequest {
  return laufzeit === 'bisEvent' ? { tier, label, untilEvent: true } : { tier, label, days: laufzeit.tage };
}
